/**
 * Splash's "thinking" musings — short pool-themed phrases that
 * cycle in the assistant bubble while waiting for the model's first
 * token. Replaces the static "Thinking…" placeholder.
 *
 * Tone: light, friendly pool-tech voice. Mix of literal status
 * ("checking your pump"), aquatic atmosphere ("listening for bubbles"),
 * and just-fun filler. Keep entries short (4–10 words) so the bubble
 * doesn't visually jump as the phrase rotates.
 *
 * The list is intentionally large so a single conversation rarely
 * sees the same phrase twice. Pick uniformly at random; suppress
 * immediate repeats inside the rotator.
 */

export const SPLASH_MUSINGS: readonly string[] = [
  // ── Active "doing something" with pool flavor ─────────────────
  'Skimming the surface for an answer',
  'Diving into your pump\'s logbook',
  'Asking the chlorinator nicely',
  'Consulting the pH chart',
  'Tracking down the right valve',
  'Following the flow lines',
  'Surveying the deep end',
  'Pulling up your pump\'s file',
  'Cross-checking the schedule',
  'Listening for bubbles',
  'Watching the impeller spin',
  'Tracing the return jet',
  'Counting laps in the data',
  'Reading the current draw',
  'Checking the pressure gauge',
  'Testing the water (digitally)',
  'Skimming for stray leaves of data',
  'Backwashing the bad ideas',
  'Topping up the chlorine of certainty',
  'Adjusting the float of inference',
  'Hopping in for a closer look',
  'Letting the dust settle',
  'Untangling the pool noodles',

  // ── Pump-specific ────────────────────────────────────────────
  'Asking your pump what it\'s up to',
  'Querying the variable-speed brain',
  'Dialing in the right preset',
  'Reading the rotor tea leaves',
  'Counting RPM on my fingers',
  'Watching the wattage curve',
  'Tracing the priming cycle',
  'Checking the impeller magnets',
  'Measuring twice, computing once',
  'Eyeing the inverter readout',
  'Spinning up the lookup',
  'Letting the pump finish its sentence',
  'Hearing it out',
  'Synchronizing with S0..S4',
  'Walking the schedule slots',
  'Polling the bearings',
  'Listening to the motor hum',
  'Grading the firmware\'s handwriting',
  'Tracing the Modbus echo',
  'Asking firmware politely',

  // ── Database / lookup flavored ───────────────────────────────
  'Ducking into the archives',
  'Pulling the runtime ledger',
  'Sampling recent telemetry',
  'Aggregating the change log',
  'Walking the timeline',
  'Spinning the rolodex',
  'Indexing the pool floor',
  'Sorting the splash-by-splash',
  'Unrolling the history scroll',
  'Cross-referencing the manual',
  'Looking it up the proper way',
  'Reading between the rows',
  'Following the breadcrumbs',
  'Asking the question correctly',
  'Triangulating the timestamps',
  'Picking the right window',

  // ── Atmospheric / playful ────────────────────────────────────
  'Floating an idea',
  'Doing a slow lap',
  'Treading water',
  'Coming up for air',
  'Catching a current of thought',
  'Letting the chlorine think',
  'Wading into the question',
  'Cannonball incoming',
  'Holding my breath',
  'Putting on the goggles',
  'Doing the backstroke',
  'Practicing my deep dive',
  'Adjusting the lane lines',
  'Counting the kickboard reps',
  'Reading the deep end',
  'Skipping a stone of logic',
  'Calling the lifeguard',
  'Whistle blowing — focus!',
  'Stretching before the jump',
  'Picking the right diving board',

  // ── Sound-effect / ASMR ──────────────────────────────────────
  'Bloop bloop',
  'Glub glub',
  'Whirrrr…',
  'Splish… splash…',
  '*muffled pump noises*',
  '*gurgling…*',
  '*pump noises*',
  '*serene pool ambience*',
  '*chlorine-scented thinking*',
  '*tile-grouting noises*',

  // ── Generic-but-on-brand status ──────────────────────────────
  'Hmm, give me a sec',
  'Working on it',
  'Almost there',
  'Just a moment',
  'Putting it together',
  'Tying the threads',
  'Lining it up',
  'Sketching the answer',
  'Drafting a response',
  'Sharpening the answer',
  'Smoothing the edges',
  'Cleaning up the prose',

  // ── Pool maintenance metaphors ───────────────────────────────
  'Vacuuming the irrelevant bits',
  'Brushing the walls of the question',
  'Shocking the data',
  'Adding stabilizer to my reasoning',
  'Topping off the basket',
  'Cleaning the skimmer of doubt',
  'Backwashing the filter of thoughts',
  'Resetting the multiport',
  'Priming the response',
  'Draining the irrelevant',
  'Refilling the answer tank',
  'Polishing the answer chrome',
  'Tightening the loose unions',
  'Lubing the o-rings',
  'Tapping the gauge twice',

  // ── Aquatic creatures cameo ──────────────────────────────────
  'Whispering to the pool koi',
  'Dolphins are checking in too',
  'Otter consultation pending',
  'Turtle\'s on the case',
  'Frogs reviewing the answer',
  'Goldfish offering opinions',

  // ── Pool gear / gadgets ──────────────────────────────────────
  'Reaching for the test strips',
  'Fetching the pool brush',
  'Grabbing the leaf rake',
  'Asking the heat pump',
  'Calibrating the salt cell',
  'Spooling up the polaris',
  'Resetting the timer clock',

  // ── Schedule / routine flavor ────────────────────────────────
  'Reading today\'s pool schedule',
  'Checking the daily log',
  'Comparing morning vs evening',
  'Running through the slots',
  'Sequencing the answer',
  'Scheduling some clarity',

  // ── Weather / season ─────────────────────────────────────────
  'Checking if the sun cooperated',
  'Factoring in the cool morning',
  'Accounting for the warm afternoon',
  'Considering recent rainfall',
  'Watching for cloud cover',
  'Reading the wind shift',

  // ── Self-aware filler ────────────────────────────────────────
  'Trying not to overthink',
  'Avoiding overcomplication',
  'Keeping it concise',
  'Resisting a long-winded answer',
  'Picking the cleanest answer',
  'Picking my words',
  'Doing the math twice',
  'Getting the units right',
  'Sanity-checking the numbers',
  'Making sure this is right',
  'Confirming with my notes',

  // ── Splash persona quips ─────────────────────────────────────
  'Splash, on it',
  'Splash, thinking…',
  'Splash is on the case',
  'Splash poolside, thinking',
  'Cooling off the thoughts',
  'Wading toward the answer',
  'Lifeguarding the response',
  'Counting tiles of context',
  'Squeegee-ing the noise',
  'Kicking off the floaties',

  // ── AquaStar-flavored ────────────────────────────────────────
  'Tuning the AquaStar dial',
  'Aligning the variable speeds',
  'Reading the AquaStar status LED',
  'Cross-checking against the V0.13 manual',
  'Calibrating the preset memory',
  'Re-syncing with the schedule slots',

  // ── Light puns ────────────────────────────────────────────────
  'Just keeping it pH-balanced',
  'Refusing to make a tile pun',
  '(Thinking is the best stroke)',
  'Going with the flow rate',
  'Pumping out a response',
  'Making waves',
  'Just a ripple — answer incoming',
  'Wave\'s about to break',

  // ── Tongue-in-cheek "AI" ─────────────────────────────────────
  'Letting the silicon think',
  'Warming up the neurons',
  'Asking the rubber duck',
  'Compiling pool wisdom',
  'Indexing thirty years of pool tech',
  'Channeling Pool Tech Mode',
  'Engaging "Splash Mode"',
  'Rolling for inspiration',
  'Beeps and blubs',
  'Crunching aquatic data',

  // ── Time-weighted thinking ───────────────────────────────────
  'Weighing the options',
  'Comparing readings',
  'Averaging the noise out',
  'Looking past the outliers',
  'Smoothing the curve',
  'Filtering the obvious',

  // ── Encouragement-toned ──────────────────────────────────────
  'Almost done — promise',
  'Hang tight',
  'On the way',
  'Coming right up',
  'Won\'t be a sec',
  'Just polishing this off',
  'Final touches',
  'About to surface',
  'Coming up to the deck',
];
